import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { buildApiUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import './AdminTiendanube.css';

function buildClienteLabel(cliente) {
  return cliente.razonSocial || cliente.cuit || `Cliente ${cliente.id}`;
}

function buildClienteMeta(cliente) {
  const parts = [];

  if (cliente.cuit) {
    parts.push(`CUIT ${cliente.cuit}`);
  }

  if (cliente.codigoPostal) {
    parts.push(`CP ${cliente.codigoPostal}`);
  }

  parts.push(`ID ${cliente.id}`);
  return parts.join(' | ');
}

function formatYesNo(value) {
  return value ? 'Si' : 'No';
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('es-AR');
}

function buildInstallHref(clienteId) {
  const query = clienteId ? `?clienteId=${encodeURIComponent(clienteId)}` : '';
  return buildApiUrl(`/tiendanube/oauth/start${query}`);
}

export default function AdminTiendanube() {
  const { authRequestJson, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [query, _setQuery] = useState('');
  const [selectedClientId, _setSelectedClientId] = useState('');
  const [tiendaNubeId, setTiendaNubeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [_saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [statusData, clientesData] = await Promise.all([
        authRequestJson('/admin/tiendanube/status?take=1000'),
        authRequestJson('/admin/catalogos/clientes?take=1000')
      ]);

      setStatus(statusData);
      setClientes(clientesData.data || []);
    } catch (err) {
      if (err?.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err?.message || 'No se pudo cargar el estado de Tiendanube.');
      }
    } finally {
      setLoading(false);
    }
  }, [authRequestJson, logout]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const selectedClient = useMemo(
    () => clientes.find((cliente) => String(cliente.id) === String(selectedClientId)) || null,
    [clientes, selectedClientId]
  );

  useEffect(() => {
    if (!selectedClient) {
      return;
    }

    setTiendaNubeId(selectedClient.tiendaNubeId ? String(selectedClient.tiendaNubeId) : '');
  }, [selectedClient]);

  const _filteredClientes = useMemo(() => {
    const term = query.trim().toLowerCase();
    const filtered = term
      ? clientes.filter((cliente) => {
          const haystack = [buildClienteLabel(cliente), buildClienteMeta(cliente), String(cliente.id)]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(term);
        })
      : clientes;

    return filtered.slice(0, 250);
  }, [clientes, query]);

  const linkedClients = useMemo(
    () =>
      clientes
        .filter((cliente) => cliente.tiendaNubeId !== null && cliente.tiendaNubeId !== undefined)
        .sort((a, b) => buildClienteLabel(a).localeCompare(buildClienteLabel(b), 'es-AR')),
    [clientes]
  );

  const summary = useMemo(
    () => ({
      configReady: !!status?.appConfigured,
      homologationReady: !!status?.app?.publicBaseUrlConfigured && !!status?.appConfigured,
      linkedClients: status?.linkedClientsCount ?? linkedClients.length,
      installations: status?.installedStoresCount ?? 0,
      webhookReady: !!status?.app?.webhookSecretConfigured
    }),
    [linkedClients.length, status]
  );

  const saveLink = async (nextTiendaNubeId = tiendaNubeId) => {
    if (!selectedClientId) {
      setError('Selecciona un cliente.');
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const normalized = String(nextTiendaNubeId || '').trim();
      const payload = {
        tiendaNubeId: normalized ? Number(normalized) : null
      };

      const data = await authRequestJson(`/admin/tiendanube/clientes/${selectedClientId}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      if (!data.ok) {
        throw new Error(data.error || 'No se pudo guardar el vinculo.');
      }

      setMessage(
        payload.tiendaNubeId
          ? `Cliente vinculado a TiendaNube ID ${payload.tiendaNubeId}.`
          : 'Vinculo de TiendaNube eliminado.'
      );
      await loadData();
    } catch (err) {
      if (err?.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err?.message || 'No se pudo guardar el vinculo.');
      }
    } finally {
      setSaving(false);
    }
  };

  const _handleSave = async (event) => {
    event.preventDefault();
    await saveLink();
  };

  const _handleClear = async () => {
    if (!selectedClientId) {
      return;
    }

    setTiendaNubeId('');
    await saveLink('');
  };

  const installHref = buildInstallHref(selectedClientId);

  const handleReconnect = async (tiendaNubeId) => {
    setError(null);
    setMessage(null);

    try {
      const data = await authRequestJson(`/admin/tiendanube/reconnect/${tiendaNubeId}`, {
        method: 'POST'
      });

      if (data?.ok && data?.authorizeUrl) {
        window.open(data.authorizeUrl, '_blank', 'noopener,noreferrer');
        setMessage(`Se abrio el flujo de reconexion para la tienda ${tiendaNubeId}.`);
      } else {
        throw new Error(data?.error || 'No se pudo iniciar la reconexion.');
      }
    } catch (err) {
      if (err?.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err?.message || 'No se pudo iniciar la reconexion.');
      }
    }
  };

  const handleSyncResources = async (tiendaNubeId) => {
    setError(null);
    setMessage(null);

    try {
      const data = await authRequestJson(`/admin/tiendanube/sync-resources/${tiendaNubeId}`, {
        method: 'POST'
      });

      if (data?.ok) {
        setMessage(`Carrier y webhooks sincronizados para la tienda ${tiendaNubeId}.`);
        await loadData();
      } else {
        throw new Error(data?.error || 'No se pudieron sincronizar los recursos.');
      }
    } catch (err) {
      if (err?.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err?.message || 'No se pudieron sincronizar los recursos.');
      }
    }
  };

  const heroMetrics = useMemo(
    () => [
      {
        label: 'Config base',
        value: formatYesNo(summary.configReady),
        note: 'Credenciales y app listas',
      },
      {
        label: 'Homologacion',
        value: formatYesNo(summary.homologationReady),
        note: 'Base publica y OAuth',
      },
      {
        label: 'Clientes vinculados',
        value: summary.linkedClients,
        note: 'Con TiendaNube ID',
      },
      {
        label: 'Instalaciones',
        value: summary.installations,
        note: 'Tiendas activas',
      },
    ],
    [summary]
  );

  const readinessFlags = useMemo(
    () => [
      { label: 'App configurada', ok: !!status?.appConfigured },
      { label: 'Client ID', ok: !!status?.app?.clientIdConfigured },
      { label: 'Client Secret', ok: !!status?.app?.clientSecretConfigured },
      { label: 'Redirect URI', ok: !!status?.app?.redirectUriConfigured },
      { label: 'Webhook secret', ok: !!status?.app?.webhookSecretConfigured },
      { label: 'Public base URL', ok: !!status?.app?.publicBaseUrlConfigured },
    ],
    [status]
  );

  const configLinks = useMemo(
    () => [
      { label: 'URL de autorizacion', value: status?.app?.authorizeUrl, note: 'Flujo OAuth y permisos' },
      { label: 'URL de inicio', value: status?.app?.installUrl, note: 'Pantalla de instalacion' },
      { label: 'Callback OAuth', value: status?.app?.oauthCallbackUrl, note: 'Retorno de autorizacion' },
      { label: 'Shipping callback', value: status?.app?.shippingRatesUrl, note: 'Cotizaciones de envio' },
      { label: 'Label callback', value: status?.app?.labelCallbackUrl, note: 'Emision de rotulos' },
      { label: 'Location prioritization', value: status?.app?.locationPrioritizationCallbackUrl, note: 'Business Rules Multi-CD' },
      { label: 'Webhook pedidos', value: status?.app?.webhookUrl, note: 'Eventos de Tiendanube' },
      { label: 'Store redact', value: status?.app?.storeRedactUrl, note: 'Privacidad y borrado' },
      { label: 'Customer redact', value: status?.app?.customerRedactUrl, note: 'Privacidad y borrado' },
      { label: 'Data request', value: status?.app?.customersDataRequestUrl, note: 'Solicitudes de datos' },
      { label: 'Admin URL', value: status?.app?.adminUrl, note: 'Panel de administracion' },
      { label: 'Preferences URL', value: status?.app?.preferencesUrl, note: 'Preferencias de la app' },
      { label: 'Privacy policy', value: status?.app?.privacyPolicyUrl, note: 'Politicas de privacidad' },
      { label: 'Support URL', value: status?.app?.supportUrl, note: 'Contacto de soporte' },
    ],
    [status]
  );

  const installationRows = status?.installedStores || [];
  const _linkedClientRows = linkedClients.slice(0, 12);

  return (
    <div className="tiendanube-page animate-fade-in">
      <section className="tiendanube-hero">
        <div className="tiendanube-hero-copy">
          <p className="tiendanube-kicker">Tiendanube</p>
          <h1>Integracion y estado</h1>
          <p className="tiendanube-hero-text">
            Vincula clientes, inicia la instalacion OAuth y revisa las URLs activas que tenes que cargar en el portal.
          </p>

          <div className="tiendanube-hero-actions">
            <Link to="/admin/tiendanube/pedidos" className="tiendanube-btn tiendanube-btn-secondary">
              Pedidos
            </Link>
            <Link to="/admin" className="tiendanube-btn tiendanube-btn-secondary">
              Volver al panel
            </Link>
            <button type="button" className="tiendanube-btn tiendanube-btn-primary" onClick={() => void loadData()} disabled={loading}>
              {loading ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>
        </div>

        <div className="tiendanube-hero-side">
          {heroMetrics.map((metric) => (
            <article key={metric.label} className="tiendanube-metric-card">
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
              <small>{metric.note}</small>
            </article>
          ))}
        </div>
      </section>

      {error ? <div className="tiendanube-alert tiendanube-alert-error">{error}</div> : null}
      {message ? <div className="tiendanube-alert tiendanube-alert-success">{message}</div> : null}

      <section className="tiendanube-grid tiendanube-grid--workspace">
        <section className="tiendanube-card tiendanube-form-card">
          <div className="tiendanube-card-head">
            <div>
              <p className="tiendanube-kicker">Vinculos</p>
              <h2>Conectar una tienda Tiendanube</h2>
              <p className="tiendanube-card-copy">
                Este boton abre la autorizacion oficial de Tiendanube. No crea una cuenta en esta pantalla ni pide cargar
                una URL de tienda.
              </p>
            </div>
          </div>

          <div className="tiendanube-selection-card tiendanube-selection-card--active">
            <span className="tiendanube-selection-label">Flujo activo</span>
            <strong>Autorizacion en cuatro pasos</strong>
            <p>1) Abrir Tiendanube. 2) Iniciar sesion si lo solicita. 3) Pulsar Aceptar y comenzar. 4) Volver automaticamente al administrador.</p>
            <small>Sin buscador, sin selector y sin exposición de cartera de clientes.</small>
          </div>

          <div className="tiendanube-form-actions">
            <button
              type="button"
              className="tiendanube-btn tiendanube-btn-primary"
              onClick={() => {
                if (!selectedClientId) {
                  setError('Selecciona un cliente para vincular la tienda.');
                  return;
                }
                setError(null);
                window.location.assign(installHref);
              }}
            >
              Autorizar tienda en Tiendanube
            </button>
            <Link to="/login" className="tiendanube-btn tiendanube-btn-secondary">
              Crear cuenta / ingresar
            </Link>
          </div>
        </section>

        <section className="tiendanube-card tiendanube-status-card">
          <div className="tiendanube-card-head">
            <div>
              <p className="tiendanube-kicker">Estado</p>
              <h2>Datos de configuracion</h2>
              <p className="tiendanube-card-copy">Revisa que las URLs y secretos necesarios esten activos antes de instalar.</p>
            </div>
          </div>

          <div className="tiendanube-status-chips">
            {readinessFlags.map((flag) => (
              <span key={flag.label} className={`tiendanube-chip ${flag.ok ? 'tiendanube-chip--ok' : 'tiendanube-chip--warn'}`}>
                {flag.label}: {flag.ok ? 'OK' : 'Pendiente'}
              </span>
            ))}
          </div>

          <div className="tiendanube-link-grid">
            {configLinks.map((item) => (
              <article key={item.label} className="tiendanube-link-card">
                <strong>{item.label}</strong>
                <span>{item.value || 'No configurada'}</span>
                <small>{item.note}</small>
              </article>
            ))}
          </div>

          <div className="tiendanube-status-note">
            <span>Mensaje</span>
            <p>{status?.message || 'Sin mensaje de estado.'}</p>
          </div>
        </section>
      </section>

      <section className="tiendanube-grid tiendanube-grid--tables">
        <section className="tiendanube-card">
          <div className="tiendanube-card-head tiendanube-card-head--tight">
            <div>
              <p className="tiendanube-kicker">Instalaciones</p>
              <h2>Tiendas activas</h2>
            </div>
            <span className="tiendanube-pill">{installationRows.length}</span>
          </div>

          <div className="tiendanube-table-shell">
            <table className="tiendanube-table">
              <thead>
                <tr>
                  <th>Tienda</th>
                  <th>Cliente</th>
                  <th>Carrier</th>
                  <th>Webhook</th>
                  <th>Estado</th>
                  <th>Accion</th>
                </tr>
              </thead>
              <tbody>
                {installationRows.length > 0 ? (
                  installationRows.map((installation) => (
                    <tr key={`${installation.tiendaNubeId}-${installation.id}`}>
                      <td>
                        <strong>{installation.tiendaNubeId}</strong>
                        <div className="tiendanube-table-muted">Instalada: {formatDateTime(installation.installedEn)}</div>
                      </td>
                      <td>
                        <strong>{installation.clienteNombre || `Cliente ${installation.clienteId || '-'}`}</strong>
                        <div className="tiendanube-table-muted">User: {installation.userId || '-'}</div>
                      </td>
                      <td>
                        <div>{installation.carrierName || '-'}</div>
                        <div className="tiendanube-table-muted">{installation.carrierOptionCode || '-'}</div>
                      </td>
                      <td>
                        <div>{installation.ultimoWebhookEvento || '-'}</div>
                        <div className="tiendanube-table-muted">{formatDateTime(installation.ultimoWebhookEn)}</div>
                      </td>
                      <td>
                        <div>{installation.activo ? 'Activa' : 'Inactiva'}</div>
                        <div className="tiendanube-table-muted">
                          {installation.requiereReconexion
                            ? 'Requiere reconexion'
                            : installation.lastError || installation.uninstalledEn
                            ? 'Con observaciones'
                            : 'OK'}
                        </div>
                      </td>
                      <td>
                        <div className="tiendanube-row-actions">
                          <button
                            type="button"
                            className="tiendanube-btn tiendanube-btn-secondary tiendanube-btn-small"
                            onClick={() => handleReconnect(installation.tiendaNubeId)}
                            disabled={loading}
                          >
                            Reconectar
                          </button>
                          <button
                            type="button"
                            className="tiendanube-btn tiendanube-btn-secondary tiendanube-btn-small"
                            onClick={() => handleSyncResources(installation.tiendaNubeId)}
                            disabled={loading}
                          >
                            Sincronizar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">
                      <p className="tiendanube-empty-state">Todavia no hay instalaciones registradas.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </section>
    </div>
  );
}
